'use client';

import { DASHBOARD_CARD_TYPES } from '@/config/dashboardWorkflows';
import { getTaskTypeFields } from '@/config/taskTypeFields';
import { useProject } from '@/contexts/ProjectContext';
import { API_ENDPOINTS, getAuthHeaders } from '@/lib/api';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useState } from 'react';
import TaskTypeFields from './TaskTypeFields';
import ActivityFeed from './ActivityFeed';
import FileUploadSection from './FileUploadSection';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'create' | 'edit';
  taskId?: number;
  kanbanColumn?: number; // Column where task is created/edited (for project tasks)
  dashboardType?: 'gtm' | 'customer' | 'finances' | 'product' | null; // Dashboard type for dashboard tasks
  dashboardStage?: number; // Stage/column for dashboard tasks
  dashboardCardType?: number | null; // Pre-selected card type for dashboard tasks
  initialSource?: string; // Initial source value for customer dashboard tasks
  onTaskSaved?: () => void;
}

interface ChecklistItem {
  id: number;
  text: string;
  completed: boolean;
}

interface Attachment {
  id: number;
  name: string;
  size: string;
  addedDate: string;
}

interface ActivityItem {
  id: number;
  member: {
    name: string;
    avatar: string;
  };
  timestamp: string;
  message: string;
}

interface Tag {
  id: number;
  name: string;
  color: string;
}

interface LinkedItem {
  id: number;
  title: string;
  taskId: string;
  type: string;
}

const statuses = [
  { id: 0, label: 'Not Started', color: 'slate' },
  { id: 1, label: 'In Progress', color: 'blue' },
  { id: 2, label: 'Testing', color: 'purple' },
  { id: 3, label: 'Done', color: 'emerald' },
];

const priorities = [
  { id: 0, label: 'Low', color: 'slate', icon: '↓' },
  { id: 1, label: 'Medium', color: 'blue', icon: '→' },
  { id: 2, label: 'High', color: 'amber', icon: '↑' },
  { id: 3, label: 'Critical', color: 'red', icon: '⚡' },
];

const fibonacciPoints = [1, 2, 3, 5, 8, 13];

// Card type mappings imported from @/config/dashboardWorkflows

export default function TaskModal({ isOpen, onClose, mode = 'create', taskId, kanbanColumn, dashboardType, dashboardStage, dashboardCardType, initialSource, onTaskSaved }: TaskModalProps) {
  const { selectedProject, selectedDashboard } = useProject();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState(1); // In Progress
  const [priority, setPriority] = useState(2); // High
  const [storyPoints, setStoryPoints] = useState(5);
  const [assignedTo, setAssignedTo] = useState<number | null>(null);
  const [sprint, setSprint] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [waitingOn, setWaitingOn] = useState('');
  const [currentKanbanColumn, setCurrentKanbanColumn] = useState<number | undefined>(kanbanColumn);
  
  // Dashboard-specific fields
  const activeDashboard = dashboardType || selectedDashboard;
  const [cardType, setCardType] = useState<number | null>(dashboardCardType || null);
  const [dashboardStageState, setDashboardStageState] = useState<number | undefined>(dashboardStage);
  const [dashboardData, setDashboardData] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showCardTypeDropdown, setShowCardTypeDropdown] = useState(false);
  const [showDashboardStatusDropdown, setShowDashboardStatusDropdown] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [linkedItems, setLinkedItems] = useState<LinkedItem[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [newComment, setNewComment] = useState('');
  const [newTag, setNewTag] = useState('');
  
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showSprintDropdown, setShowSprintDropdown] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [activeSidebar, setActiveSidebar] = useState<'details' | 'activity' | null>(null);
  
  const toggleSidebar = (sidebar: 'details' | 'activity') => {
    setActiveSidebar(activeSidebar === sidebar ? null : sidebar);
  };
  
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  
  const [sprints, setSprints] = useState<Array<{ id: number; name: string; status: string }>>([]);
  const [teamMembers, setTeamMembers] = useState<Array<{ id: number; name: string; email: string; avatar: string }>>([]);

  // Tiptap editor instance
  const editor = useEditor({
    extensions: [StarterKit],
    content: description,
    onUpdate: ({ editor: editorInstance }) => {
      setDescription(editorInstance.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[150px] px-4 py-3 text-sm text-white',
        'data-placeholder': 'Add a description...',
      },
    },
  });

  // Update editor content when description changes externally (e.g., when loading a task)
  useEffect(() => {
    if (editor && description !== editor.getHTML()) {
      editor.commands.setContent(description || '');
    }
  }, [description, editor]);

  // Cleanup editor when modal closes
  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  useEffect(() => {
    if (isOpen) {
      setCurrentKanbanColumn(kanbanColumn);
      setDashboardStageState(dashboardStage);
      if (dashboardCardType !== undefined) {
        setCardType(dashboardCardType);
      }
      if (mode === 'edit' && taskId) {
        fetchTaskData();
      } else {
        resetForm();
      }
      if (!activeDashboard) {
        // Only fetch sprints for project tasks
        fetchSprints();
      }
      // Fetch team members for both project and dashboard tasks (needed for user_select fields)
      fetchTeamMembers();
    }
  }, [isOpen, mode, taskId, kanbanColumn, dashboardStage, dashboardCardType, initialSource]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setStatus(1);
    setPriority(2);
    setStoryPoints(5);
    setAssignedTo(null);
    setSprint(null);
    setDueDate('');
    setWaitingOn('');
    setCardType(dashboardCardType || null);
    setDashboardStageState(dashboardStage);
    // Initialize dashboardData with initialSource if provided
    setDashboardData(initialSource ? { source: initialSource } : {});
    setFieldErrors({});
    setChecklist([]);
    setTags([]);
    setLinkedItems([]);
    setAttachments([]);
    setActivity([]);
    setError('');
  };

  const fetchTaskData = async () => {
    if (!taskId) return;
    
    try {
      setFetching(true);
      // For dashboard tasks, include the dashboard parameter so the backend can find it
      let url = `${API_ENDPOINTS.TASKS.LIST}${taskId}/`;
      if (activeDashboard !== null) {
        const dashboardNumber = {
          gtm: 0,
          customer: 1,
          finances: 2,
          product: 3,
        }[activeDashboard];
        if (dashboardNumber !== undefined) {
          url += `?dashboard=${dashboardNumber}`;
        }
      }
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const task = await response.json();
        setTitle(task.title || '');
        setDescription(task.description || '');
        setPriority(task.priority ?? 2);
        setAssignedTo(task.assigned_to?.id || null);
        setDueDate(task.due_date || '');
        setWaitingOn(task.waiting_on || '');
        
        // Load dashboard-specific fields
        if (task.dashboard !== null && task.dashboard !== undefined) {
          setCardType(task.card_type);
          setDashboardStageState(task.stage ?? 0);
          // Load dashboard_data from task.dashboard_data or task.data
          const data = task.dashboard_data || task.data || {};
          setDashboardData(data);
        } else {
          // Project task fields
          setStatus(task.status ?? 1);
          setStoryPoints(task.estimated_hours || 5);
          const sprintId = typeof task.sprint === 'number' ? task.sprint : task.sprint?.id;
          setSprint(sprintId ?? null);
          setCurrentKanbanColumn(task.kanban_column);
        }
        // TODO: Load checklist, tags, attachments, activity from task data
      } else {
        setError('Failed to load task data');
      }
    } catch (error) {
      setError('Unable to load task data');
    } finally {
      setFetching(false);
    }
  };

  const fetchSprints = async () => {
    if (!selectedProject) return;
    
    try {
      const response = await fetch(`${API_ENDPOINTS.SPRINTS}?project=${selectedProject.id}`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setSprints(data.results || data);
      }
    } catch (error) {
      // Error fetching sprints
    }
  };

  const fetchTeamMembers = async () => {
    try {
      let members: Array<{ id: number; name: string; email: string; avatar: string }> = [];
      
      if (selectedProject) {
        // Fetch from project members
        const response = await fetch(`${API_ENDPOINTS.PROJECTS.DETAIL(selectedProject.id)}`, {
          headers: getAuthHeaders(),
        });

        if (response.ok) {
          const project = await response.json();
          if (project.members && Array.isArray(project.members)) {
            members = project.members.map((m: { member: { id: number; full_name?: string; email: string } }) => ({
              id: m.member.id,
              name: m.member.full_name || m.member.email,
              email: m.member.email,
              avatar: (m.member.full_name || m.member.email || 'U').charAt(0).toUpperCase(),
            }));
          }
        }
      } else if (activeDashboard) {
        // For dashboard tasks, we might need to fetch all account members
        // For now, use an empty array or fetch from a different endpoint
        // TODO: Add API endpoint to fetch all account members for dashboard tasks
        members = [];
      }
      
      setTeamMembers(members);
    } catch (error) {
      // Error fetching team members
    }
  };

  const handleSave = async () => {
    // Validation
    if (!title.trim()) {
      setError(`${cardTypeLabel} title is required`);
      return;
    }

    // For dashboard tasks, validate card_type
    if (activeDashboard && !cardType) {
      setError('Please select a card type');
      return;
    }

    // Validate required fields for dashboard tasks
    if (activeDashboard && cardType) {
      const config = getTaskTypeFields(activeDashboard, cardType);
      const errors: Record<string, string> = {};
      
      if (config) {
        config.fields.forEach((field) => {
          if (field.required && field.name !== 'title') {
            const value = dashboardData[field.name];
            if (!value || (typeof value === 'string' && !value.trim())) {
              errors[field.name] = `${field.label} is required`;
            }
          }
        });
      }
      
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setError('Please fill in all required fields');
        return;
      }
      
      setFieldErrors({});
    }

    // For project tasks, validate project
    if (!activeDashboard && !selectedProject) {
      setError('Please select a project first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let url = mode === 'edit' && taskId
        ? `${API_ENDPOINTS.TASKS.LIST}${taskId}/`
        : API_ENDPOINTS.TASKS.LIST;

      // Dashboard tasks need the dashboard query parameter for the backend to find them
      if (mode === 'edit' && taskId && activeDashboard) {
        const dashNum = { gtm: 0, customer: 1, finances: 2, product: 3 }[activeDashboard];
        if (dashNum !== undefined) {
          url += `?dashboard=${dashNum}`;
        }
      }
      
      const method = mode === 'edit' ? 'PATCH' : 'POST';

      const requestBody: Record<string, unknown> = {
        title,
        description: description || '',
      };

      if (activeDashboard) {
        // Dashboard task
        const dashboardNumber = {
          gtm: 0,
          customer: 1,
          finances: 2,
          product: 3,
        }[activeDashboard];
        
        requestBody.dashboard = dashboardNumber;
        requestBody.card_type = cardType;
        requestBody.stage = dashboardStageState !== undefined ? dashboardStageState : 0;
        requestBody.status = 0; // Dashboard tasks use stage, not status
        requestBody.priority = priority;
        // Include dashboard-specific data
        requestBody.data = dashboardData;
      } else {
        // Project task
        requestBody.project = selectedProject!.id;
        requestBody.status = status;
        requestBody.priority = priority;
        requestBody.estimated_hours = storyPoints;
        requestBody.kanban_column = currentKanbanColumn;
      }

      // Only include these if they have values
      if (assignedTo !== null) {
        requestBody.assigned_to_id = assignedTo;
      } else {
        requestBody.assigned_to_id = null;
      }

      if (!activeDashboard) {
        // Sprint only applies to project tasks
        if (sprint !== null) {
          requestBody.sprint_id = sprint;
        } else {
          requestBody.sprint_id = null;
        }
      }

      if (dueDate) {
        requestBody.due_date = dueDate;
      }

      // Always sent so clearing the box clears it on the task
      requestBody.waiting_on = waitingOn.trim() || null;

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const task = await response.json();
        resetForm();
        onClose();
        if (onTaskSaved) {
          onTaskSaved();
        }
      } else {
        const errorData = await response.json();
        // Handle validation errors from backend
        let errorMessage = errorData.detail || errorData.message;
        if (errorData.card_type) {
          errorMessage = Array.isArray(errorData.card_type) 
            ? errorData.card_type.join(', ') 
            : errorData.card_type;
        }
        setError(errorMessage || `Failed to ${mode === 'edit' ? 'update' : 'create'} task`);
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!taskId || mode !== 'edit') return;
    
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.TASKS.LIST}${taskId}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        resetForm();
        onClose();
        if (onTaskSaved) {
          onTaskSaved();
        }
      } else {
        setError('Failed to delete task');
      }
    } catch (error) {
      setError('Unable to delete task');
    }
  };

  const toggleChecklistItem = (id: number) => {
    setChecklist(prev => prev.map(item => 
      item.id === id ? { ...item, completed: !item.completed } : item
    ));
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    setChecklist(prev => [...prev, {
      id: Date.now(),
      text: newChecklistItem,
      completed: false,
    }]);
    setNewChecklistItem('');
  };

  const removeChecklistItem = (id: number) => {
    setChecklist(prev => prev.filter(item => item.id !== id));
  };

  const addTag = () => {
    if (!newTag.trim()) return;
    const colors = ['blue', 'purple', 'emerald', 'amber', 'red'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    setTags(prev => [...prev, {
      id: Date.now(),
      name: newTag,
      color: randomColor,
    }]);
    setNewTag('');
  };

  const removeTag = (id: number) => {
    setTags(prev => prev.filter(tag => tag.id !== id));
  };

  const addComment = () => {
    if (!newComment.trim()) return;
    // TODO: Send comment to backend
    setActivity(prev => [{
      id: Date.now(),
      member: { name: 'You', avatar: 'cyan' },
      timestamp: 'Just now',
      message: newComment,
    }, ...prev]);
    setNewComment('');
  };

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      slate: 'bg-slate-500',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      amber: 'bg-amber-500',
      emerald: 'bg-emerald-500',
      red: 'bg-red-500',
    };
    return colors[color] || colors.slate;
  };

  const getStatusBgColor = (color: string) => {
    const colors: Record<string, string> = {
      slate: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
      blue: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      purple: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      amber: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      emerald: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      red: 'bg-red-500/20 text-red-300 border-red-500/30',
    };
    return colors[color] || colors.slate;
  };

  const getTagColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-500/20 text-blue-300',
      purple: 'bg-purple-500/20 text-purple-300',
      emerald: 'bg-emerald-500/20 text-emerald-300',
      amber: 'bg-amber-500/20 text-amber-300',
      red: 'bg-red-500/20 text-red-300',
    };
    return colors[color] || colors.blue;
  };

  const getAvatarGradient = (color: string) => {
    const gradients: Record<string, string> = {
      cyan: 'from-cyan-400 to-blue-500',
      emerald: 'from-emerald-400 to-teal-500',
      purple: 'from-purple-400 to-pink-500',
      slate: 'from-slate-400 to-slate-500',
    };
    return gradients[color] || gradients.slate;
  };

  if (!isOpen) return null;

  const currentStatus = statuses.find(s => s.id === status) || statuses[1];
  const currentPriority = priorities.find(p => p.id === priority) || priorities[2];
  const currentSprint = sprints.find(s => s.id === sprint);
  const currentAssignee = teamMembers.find(m => m.id === assignedTo);
  const completedCount = checklist.filter(item => item.completed).length;
  const checklistProgress = checklist.length > 0 ? (completedCount / checklist.length) * 100 : 0;

  // Get card type label for display
  const getCardTypeLabel = () => {
    if (activeDashboard && cardType !== null) {
      return DASHBOARD_CARD_TYPES[activeDashboard]?.find(ct => ct.id === cardType)?.label || 'Task';
    }
    return 'Task';
  };

  const cardTypeLabel = getCardTypeLabel();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Background Stars */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              width: `${Math.random() * 2 + 0.5}px`,
              height: `${Math.random() * 2 + 0.5}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.4 + 0.1,
            }}
          />
        ))}
      </div>

      {/* Modal Backdrop */}
      <div className="fixed inset-0 bg-gray-300/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-5xl bg-[#0f1429] rounded-xl border border-[#1a1f3a] shadow-2xl overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a1f3a] bg-[#0a0e27]">
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-1 rounded text-xs font-medium border bg-orange-500/20 text-orange-300 border-orange-500/30">
              ◉ {cardTypeLabel}
            </span>
            {mode === 'edit' && taskId && (
              <>
                <span className="text-gray-500 text-sm">
                  {activeDashboard 
                    ? `#${activeDashboard.toUpperCase()}-${taskId}`
                    : `#${selectedProject?.initials || 'GEN'}-${taskId}`
                  }
                </span>
                <span className="text-gray-600 text-xs">Created {new Date().toLocaleDateString()}</span>
              </>
            )}
            {mode === 'create' && (
              <span className="text-gray-500 text-sm">New {cardTypeLabel}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => toggleSidebar('details')}
              className={`p-1.5 hover:bg-[#1a1f3a] rounded transition-colors ${
                activeSidebar === 'details' ? 'text-orange-400 bg-[#1a1f3a]' : 'text-gray-400 hover:text-gray-300'
              }`}
              title={`${cardTypeLabel} details`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </button>
            <button 
              onClick={() => toggleSidebar('activity')}
              className={`p-1.5 hover:bg-[#1a1f3a] rounded transition-colors ${
                activeSidebar === 'activity' ? 'text-orange-400 bg-[#1a1f3a]' : 'text-gray-400 hover:text-gray-300'
              }`}
              title="Activity"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300 p-1.5 hover:bg-[#1a1f3a] rounded transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-6 py-3 bg-red-500/10 border-b border-red-500/50">
            <div className="text-red-400 text-sm">{error}</div>
          </div>
        )}

        {/* Loading state */}
        {fetching && (
          <div className="px-6 py-4 text-gray-400 text-sm">Loading task data...</div>
        )}

        {/* Modal Body */}
        <div className="flex max-h-[70vh] overflow-hidden relative">
          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {/* Title */}
            <div className="mb-4">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={`${cardTypeLabel} title`}
                className="w-full bg-transparent text-xl font-semibold text-white placeholder-gray-500 focus:outline-none border-b border-transparent focus:border-[#1a1f3a] pb-2"
              />
            </div>

            {/* Description - Hidden for Whitepaper (uses Abstract instead) */}
            {!(activeDashboard === 'gtm' && cardType === 1) && (
            <div className="mb-6">
              <label className="block text-xs text-gray-400 mb-2">Description</label>
              <div className="bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg overflow-hidden focus-within:border-orange-500/50">
                {/* Toolbar */}
                {editor && (
                  <div className="flex items-center gap-1 p-2 border-b border-[#2d1b3d] bg-[#0a0e27] flex-wrap">
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      className={`p-1.5 rounded hover:bg-[#1a1f3a] transition-colors ${
                        editor.isActive('bold') ? 'bg-[#1a1f3a] text-orange-400' : 'text-gray-400'
                      }`}
                      title="Bold"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      className={`p-1.5 rounded hover:bg-[#1a1f3a] transition-colors ${
                        editor.isActive('italic') ? 'bg-[#1a1f3a] text-orange-400' : 'text-gray-400'
                      }`}
                      title="Italic"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                    </button>
                    <div className="w-px h-6 bg-[#2d1b3d] mx-1" />
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                      className={`p-1.5 rounded hover:bg-[#1a1f3a] transition-colors ${
                        editor.isActive('heading', { level: 1 }) ? 'bg-[#1a1f3a] text-orange-400' : 'text-gray-400'
                      }`}
                      title="Heading 1"
                    >
                      <span className="text-xs font-bold">H1</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                      className={`p-1.5 rounded hover:bg-[#1a1f3a] transition-colors ${
                        editor.isActive('heading', { level: 2 }) ? 'bg-[#1a1f3a] text-orange-400' : 'text-gray-400'
                      }`}
                      title="Heading 2"
                    >
                      <span className="text-xs font-bold">H2</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                      className={`p-1.5 rounded hover:bg-[#1a1f3a] transition-colors ${
                        editor.isActive('heading', { level: 3 }) ? 'bg-[#1a1f3a] text-orange-400' : 'text-gray-400'
                      }`}
                      title="Heading 3"
                    >
                      <span className="text-xs font-bold">H3</span>
                    </button>
                    <div className="w-px h-6 bg-[#2d1b3d] mx-1" />
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                      className={`p-1.5 rounded hover:bg-[#1a1f3a] transition-colors ${
                        editor.isActive('bulletList') ? 'bg-[#1a1f3a] text-orange-400' : 'text-gray-400'
                      }`}
                      title="Bullet List"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                      className={`p-1.5 rounded hover:bg-[#1a1f3a] transition-colors ${
                        editor.isActive('orderedList') ? 'bg-[#1a1f3a] text-orange-400' : 'text-gray-400'
                      }`}
                      title="Numbered List"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                      </svg>
                    </button>
                    <div className="w-px h-6 bg-[#2d1b3d] mx-1" />
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleBlockquote().run()}
                      className={`p-1.5 rounded hover:bg-[#1a1f3a] transition-colors ${
                        editor.isActive('blockquote') ? 'bg-[#1a1f3a] text-orange-400' : 'text-gray-400'
                      }`}
                      title="Quote"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                      className={`p-1.5 rounded hover:bg-[#1a1f3a] transition-colors ${
                        editor.isActive('codeBlock') ? 'bg-[#1a1f3a] text-orange-400' : 'text-gray-400'
                      }`}
                      title="Code Block"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                    </button>
                  </div>
                )}
                {/* Editor Content */}
                <EditorContent editor={editor} />
              </div>
            </div>
            )}

            {/* Task Type Specific Fields */}
            {activeDashboard && cardType && (
              <TaskTypeFields
                dashboardType={activeDashboard}
                cardType={cardType}
                data={dashboardData}
                onChange={setDashboardData}
                errors={fieldErrors}
                teamMembers={teamMembers}
              />
            )}

            {/* Checklist */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs text-gray-400">Checklist</label>
                {checklist.length > 0 && (
                  <span className="text-xs text-gray-500">{completedCount}/{checklist.length} completed</span>
                )}
              </div>
              
              {/* Progress Bar */}
              {checklist.length > 0 && (
                <div className="w-full bg-[#1a1f3a] rounded-full h-1.5 mb-3">
                  <div 
                    className="bg-green-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${checklistProgress}%` }}
                  />
                </div>
              )}

              <div className="space-y-2">
                {checklist.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#1a1f3a]/50 group"
                  >
                    <button
                      onClick={() => toggleChecklistItem(item.id)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        item.completed
                          ? 'bg-green-500 border-green-500'
                          : 'border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      {item.completed && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                    <span className={`flex-1 text-sm ${item.completed ? 'text-gray-500 line-through' : 'text-gray-300'}`}>
                      {item.text}
                    </span>
                    <button 
                      onClick={() => removeChecklistItem(item.id)}
                      className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
                    placeholder="Add checklist item"
                    className="flex-1 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
                  />
                  <button
                    onClick={addChecklistItem}
                    className="px-3 py-2 bg-[#1a1f3a] border border-[#2d1b3d] hover:border-orange-500 text-white rounded-lg text-sm transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Attachments - Only show for edit mode */}
            {mode === 'edit' && taskId && (
              <div className="mb-6">
                <label className="block text-xs text-gray-400 mb-2">Attachments</label>
                <FileUploadSection taskId={taskId} />
              </div>
            )}

          </div>

          {/* Sidebar Overlay */}
          {activeSidebar && (
            <div 
              className="absolute inset-0 bg-black/50 z-10"
              onClick={() => setActiveSidebar(null)}
            />
          )}
          
          {/* Details Sidebar */}
          <div className={`absolute right-0 top-0 h-full w-64 border-l border-[#1a1f3a] bg-[#0a0e27] p-4 space-y-5 overflow-y-auto transition-transform duration-300 z-20 shadow-2xl ${
            activeSidebar === 'details' ? 'translate-x-0' : 'translate-x-full'
          }`}>
            {/* Sidebar Header */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#1a1f3a]">
              <h3 className="text-sm font-medium text-gray-300">{cardTypeLabel} Details</h3>
              <button 
                onClick={() => setActiveSidebar(null)}
                className="text-gray-400 hover:text-gray-300 p-1 hover:bg-[#1a1f3a] rounded transition-colors"
                title="Close sidebar"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Card Type (Dashboard only) */}
            {activeDashboard && (
              <div className="relative">
                <label className="block text-xs text-gray-400 mb-1.5">Card Type</label>
                <button
                  onClick={() => {
                    setShowCardTypeDropdown(!showCardTypeDropdown);
                    setShowStatusDropdown(false);
                    setShowPriorityDropdown(false);
                    setShowSprintDropdown(false);
                    setShowAssigneeDropdown(false);
                    setShowDashboardStatusDropdown(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg text-sm text-gray-300 hover:border-gray-600 transition-colors"
                >
                  <span className="flex-1 text-left">
                    {cardType !== null 
                      ? DASHBOARD_CARD_TYPES[activeDashboard]?.find(ct => ct.id === cardType)?.label || 'Select type'
                      : 'Select type'}
                  </span>
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showCardTypeDropdown && activeDashboard && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowCardTypeDropdown(false)} />
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg shadow-xl z-20 py-1">
                      {DASHBOARD_CARD_TYPES[activeDashboard]?.map((ct) => (
                        <button
                          key={ct.id}
                          onClick={() => { 
                            setCardType(ct.id); 
                            // Reset dashboard data when card type changes
                            if (ct.id !== cardType) {
                              setDashboardData({});
                              setFieldErrors({});
                            }
                            setShowCardTypeDropdown(false); 
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#2d1b3d] transition-colors ${
                            cardType === ct.id ? 'bg-[#2d1b3d]/50' : ''
                          }`}
                        >
                          <span className="text-gray-300">{ct.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Status (Dashboard only) */}
            {activeDashboard && (
              <div className="relative">
                <label className="block text-xs text-gray-400 mb-1.5">Status</label>
                <button
                  onClick={() => {
                    setShowDashboardStatusDropdown(!showDashboardStatusDropdown);
                    setShowCardTypeDropdown(false);
                    setShowPriorityDropdown(false);
                    setShowAssigneeDropdown(false);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    activeDashboard === 'gtm' 
                      ? getStatusBgColor(['slate', 'blue', 'purple', 'amber', 'emerald'][dashboardStageState || 0] as string)
                      : getStatusBgColor('slate')
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${
                    activeDashboard === 'gtm'
                      ? getStatusColor(['slate', 'blue', 'purple', 'amber', 'emerald'][dashboardStageState || 0] as string)
                      : getStatusColor('slate')
                  }`}></span>
                  {activeDashboard === 'gtm' 
                    ? ['Ideation', 'Drafting', 'Review', 'Scheduled', 'Published'][dashboardStageState || 0]
                    : activeDashboard === 'customer'
                    ? ['Prospect', 'Contacted', 'Discovery', 'Proposal', 'Won'][dashboardStageState || 0]
                    : ['Research', 'Drafting', 'Submitted', 'In Review', 'Closed'][dashboardStageState || 0]
                  }
                  <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showDashboardStatusDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowDashboardStatusDropdown(false)} />
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg shadow-xl z-20 py-1">
                      {(activeDashboard === 'gtm' 
                        ? ['Ideation', 'Drafting', 'Review', 'Scheduled', 'Published']
                        : activeDashboard === 'customer'
                        ? ['Prospect', 'Contacted', 'Discovery', 'Proposal', 'Won']
                        : ['Research', 'Drafting', 'Submitted', 'In Review', 'Closed']
                      ).map((label, index) => (
                        <button
                          key={index}
                          onClick={() => { 
                            setDashboardStageState(index); 
                            setShowDashboardStatusDropdown(false); 
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#2d1b3d] transition-colors ${
                            dashboardStageState === index ? 'bg-[#2d1b3d]/50' : ''
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${
                            activeDashboard === 'gtm'
                              ? getStatusColor(['slate', 'blue', 'purple', 'amber', 'emerald'][index] as string)
                              : getStatusColor('slate')
                          }`}></span>
                          <span className="text-gray-300">{label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Status (Project only) */}
            {!activeDashboard && (
              <div className="relative">
                <label className="block text-xs text-gray-400 mb-1.5">Status</label>
                <button
                  onClick={() => {
                    setShowStatusDropdown(!showStatusDropdown);
                    setShowPriorityDropdown(false);
                    setShowSprintDropdown(false);
                    setShowAssigneeDropdown(false);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${getStatusBgColor(currentStatus.color)}`}
                >
                  <span className={`w-2 h-2 rounded-full ${getStatusColor(currentStatus.color)}`}></span>
                  {currentStatus.label}
                  <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showStatusDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowStatusDropdown(false)} />
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg shadow-xl z-20 py-1">
                      {statuses.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => { setStatus(s.id); setShowStatusDropdown(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#2d1b3d] transition-colors ${
                            status === s.id ? 'bg-[#2d1b3d]/50' : ''
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${getStatusColor(s.color)}`}></span>
                          <span className="text-gray-300">{s.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Assignee */}
            <div className="relative">
              <label className="block text-xs text-gray-400 mb-1.5">Assignee</label>
              <button
                onClick={() => {
                  setShowAssigneeDropdown(!showAssigneeDropdown);
                  setShowStatusDropdown(false);
                  setShowPriorityDropdown(false);
                  setShowSprintDropdown(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg text-sm text-gray-300 hover:border-gray-600 transition-colors"
              >
                {currentAssignee ? (
                  <>
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${getAvatarGradient('cyan')} flex items-center justify-center text-xs text-white`}>
                      {currentAssignee.avatar}
                    </div>
                    <span className="flex-1 text-left">{currentAssignee.name}</span>
                  </>
                ) : (
                  <span className="flex-1 text-left text-gray-500">Unassigned</span>
                )}
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showAssigneeDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowAssigneeDropdown(false)} />
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg shadow-xl z-20 py-1">
                    <button
                      onClick={() => { setAssignedTo(null); setShowAssigneeDropdown(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#2d1b3d] transition-colors ${
                        assignedTo === null ? 'bg-[#2d1b3d]/50' : ''
                      }`}
                    >
                      <span className="text-gray-300">Unassigned</span>
                    </button>
                    {teamMembers.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => { setAssignedTo(member.id); setShowAssigneeDropdown(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#2d1b3d] transition-colors ${
                          assignedTo === member.id ? 'bg-[#2d1b3d]/50' : ''
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${getAvatarGradient('cyan')} flex items-center justify-center text-xs text-white`}>
                          {member.avatar}
                        </div>
                        <span className="text-gray-300">{member.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Sprint (Project only) */}
            {!activeDashboard && (
              <div className="relative">
                <label className="block text-xs text-gray-400 mb-1.5">Sprint</label>
                <button
                  onClick={() => {
                    setShowSprintDropdown(!showSprintDropdown);
                    setShowStatusDropdown(false);
                    setShowPriorityDropdown(false);
                    setShowAssigneeDropdown(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg text-sm text-gray-300 hover:border-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="flex-1 text-left">{currentSprint?.name || 'No Sprint'}</span>
                  {currentSprint?.status === 'active' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">Active</span>
                  )}
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showSprintDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowSprintDropdown(false)} />
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg shadow-xl z-20 py-1">
                      <button
                        onClick={() => { setSprint(null); setShowSprintDropdown(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#2d1b3d] transition-colors ${
                          sprint === null ? 'bg-[#2d1b3d]/50' : ''
                        }`}
                      >
                        <span className="text-gray-300">No Sprint</span>
                      </button>
                      {sprints.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => { setSprint(s.id); setShowSprintDropdown(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#2d1b3d] transition-colors ${
                            sprint === s.id ? 'bg-[#2d1b3d]/50' : ''
                          }`}
                        >
                          <span className="text-gray-300 flex-1">{s.name}</span>
                          {s.status === 'active' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">Active</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Priority */}
            <div className="relative">
              <label className="block text-xs text-gray-400 mb-1.5">Priority</label>
              <button
                onClick={() => {
                  setShowPriorityDropdown(!showPriorityDropdown);
                  setShowStatusDropdown(false);
                  setShowSprintDropdown(false);
                  setShowAssigneeDropdown(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${getStatusBgColor(currentPriority.color)}`}
              >
                <span>{currentPriority.icon}</span>
                {currentPriority.label}
                <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showPriorityDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowPriorityDropdown(false)} />
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg shadow-xl z-20 py-1">
                    {priorities.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => { setPriority(p.id); setShowPriorityDropdown(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#2d1b3d] transition-colors ${
                          priority === p.id ? 'bg-[#2d1b3d]/50' : ''
                        }`}
                      >
                        <span>{p.icon}</span>
                        <span className="text-gray-300">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Story Points (Project only) */}
            {!activeDashboard && (
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Story Points</label>
                <div className="flex gap-1">
                  {fibonacciPoints.map((point) => (
                    <button
                      key={point}
                      onClick={() => setStoryPoints(point)}
                      className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                        storyPoints === point
                          ? 'bg-orange-500 text-white'
                          : 'bg-[#1a1f3a] text-gray-400 hover:bg-[#2d1b3d] border border-[#2d1b3d]'
                      }`}
                    >
                      {point}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Due Date */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500/50"
              />
            </div>

            {/* Waiting On */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Waiting On</label>
              <input
                type="text"
                value={waitingOn}
                onChange={(e) => setWaitingOn(e.target.value)}
                placeholder="e.g. co-founder"
                maxLength={140}
                className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Tags</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {tags.map((tag) => (
                  <span key={tag.id} className={`px-2 py-1 ${getTagColor(tag.color)} rounded text-xs flex items-center gap-1`}>
                    {tag.name}
                    <button onClick={() => removeTag(tag.id)} className="hover:opacity-70">×</button>
                  </span>
                ))}
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  placeholder="+ Add"
                  className="px-2 py-1 border border-dashed border-gray-600 text-gray-500 hover:text-gray-400 hover:border-gray-500 rounded text-xs transition-colors bg-transparent focus:outline-none focus:border-orange-500/50"
                />
              </div>
            </div>

            {/* Linked Items */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Linked Items</label>
              <div className="space-y-1.5">
                {linkedItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 p-2 bg-[#1a1f3a]/50 rounded border border-[#2d1b3d] text-xs">
                    <span className="text-blue-400">★</span>
                    <span className="text-gray-300 flex-1">{item.title}</span>
                    <span className="text-gray-600">{item.taskId}</span>
                  </div>
                ))}
                <button className="w-full px-2 py-1.5 border border-dashed border-gray-600 text-gray-500 hover:text-gray-400 hover:border-gray-500 rounded text-xs transition-colors">
                  + Link item
                </button>
              </div>
            </div>
          </div>

          {/* Activity Sidebar */}
          <div className={`absolute right-0 top-0 h-full w-80 border-l border-[#1a1f3a] bg-[#0a0e27] p-4 overflow-y-auto transition-transform duration-300 z-20 shadow-2xl ${
            activeSidebar === 'activity' ? 'translate-x-0' : 'translate-x-full'
          }`}>
            {/* Sidebar Header */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#1a1f3a]">
              <h3 className="text-sm font-medium text-gray-300">Activity</h3>
              <button
                onClick={() => setActiveSidebar(null)}
                className="text-gray-400 hover:text-gray-300 p-1 hover:bg-[#1a1f3a] rounded transition-colors"
                title="Close sidebar"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Activity Feed - Only show for edit mode */}
            {mode === 'edit' && taskId ? (
              <ActivityFeed taskId={taskId} />
            ) : (
              <div className="text-gray-400 text-sm text-center py-4">
                Save the task to see activity
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#1a1f3a] bg-[#0a0e27]">
          {mode === 'edit' && taskId && (
            <button 
              onClick={handleDelete}
              className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          )}
          {mode === 'create' && <div />}
          <div className="flex items-center gap-3 ml-auto">
            <button 
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              disabled={loading}
              className="px-5 py-2 bg-orange-500 hover:bg-orange-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

